import { ALLOWED_UPLOAD_EXTENSIONS, MAX_FILES_PER_UPLOAD, MAX_UPLOAD_BYTES } from "@sagot/constants";
import { useId, useRef, useState, type DragEvent, type ReactNode } from "react";
import { cn } from "../lib/cn";
import { Icon } from "./icon";

export type FileRejectionReason = "type" | "size" | "empty" | "count";

export interface FileRejection {
  file: File;
  reason: FileRejectionReason;
}

export interface FileValidationOptions {
  /** Lower-case extensions incl. dot, e.g. [".pdf", ".docx"]. */
  accept?: readonly string[];
  maxBytes?: number;
  maxFiles?: number;
}

function extensionOf(name: string): string {
  const dot = name.lastIndexOf(".");
  return dot === -1 ? "" : name.slice(dot).toLowerCase();
}

/** Client pre-validation (the server re-validates MIME + size + AV). */
export function validateFiles(files: readonly File[], options: FileValidationOptions = {}): { accepted: File[]; rejected: FileRejection[] } {
  const { accept = ALLOWED_UPLOAD_EXTENSIONS, maxBytes = MAX_UPLOAD_BYTES, maxFiles = MAX_FILES_PER_UPLOAD } = options;
  const allowed = new Set(accept.map((ext) => ext.toLowerCase()));
  const accepted: File[] = [];
  const rejected: FileRejection[] = [];
  for (const file of files) {
    if (!allowed.has(extensionOf(file.name))) rejected.push({ file, reason: "type" });
    else if (file.size <= 0) rejected.push({ file, reason: "empty" });
    else if (file.size > maxBytes) rejected.push({ file, reason: "size" });
    else if (accepted.length >= maxFiles) rejected.push({ file, reason: "count" });
    else accepted.push(file);
  }
  return { accepted, rejected };
}

export interface FileDropzoneLabels {
  title: string;
  /** e.g. "PDF, DOCX, XLSX, PPTX, JPG/PNG · max 50 MB each" */
  hint: string;
  dragActive: string;
  rejected: (rejection: FileRejection) => string;
}

const DEFAULT_LABELS: FileDropzoneLabels = {
  title: "Drop files here or click to browse",
  hint: "PDF, DOCX, XLSX, PPTX, JPG/PNG · max 50 MB each",
  dragActive: "Release to upload",
  rejected: ({ file, reason }) =>
    ({
      type: `${file.name}: this file type isn't supported`,
      size: `${file.name}: larger than the size limit`,
      empty: `${file.name}: the file is empty`,
      count: `${file.name}: too many files at once`,
    })[reason],
};

export interface FileDropzoneProps extends FileValidationOptions {
  onFiles: (files: File[]) => void;
  onReject?: (rejections: FileRejection[]) => void;
  multiple?: boolean;
  disabled?: boolean;
  labels?: Partial<FileDropzoneLabels>;
  className?: string;
  children?: ReactNode;
}

/**
 * `[data-dropzone]`: a real button (Enter/Space opens the picker) that also accepts drag & drop.
 * Emits validated `File[]`; rejections are listed in a live region.
 */
export function FileDropzone({ onFiles, onReject, multiple = true, disabled, labels, className, children, ...validation }: FileDropzoneProps) {
  const l = { ...DEFAULT_LABELS, ...labels };
  const inputRef = useRef<HTMLInputElement>(null);
  const hintId = useId();
  const [dragging, setDragging] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  const handle = (list: FileList | null) => {
    if (!list || disabled) return;
    const { accepted, rejected } = validateFiles(Array.from(list), { ...validation, maxFiles: multiple ? validation.maxFiles : 1 });
    setErrors(rejected.map(l.rejected));
    if (rejected.length > 0) onReject?.(rejected);
    if (accepted.length > 0) onFiles(accepted);
  };

  const onDrop = (event: DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setDragging(false);
    handle(event.dataTransfer.files);
  };

  const accept = (validation.accept ?? ALLOWED_UPLOAD_EXTENSIONS).join(",");

  return (
    <div className={className}>
      <button
        type="button"
        disabled={disabled}
        aria-describedby={hintId}
        data-dragging={dragging || undefined}
        onClick={() => inputRef.current?.click()}
        onDragOver={(event) => {
          event.preventDefault();
          if (!disabled) setDragging(true);
        }}
        onDragLeave={() => {
          setDragging(false);
        }}
        onDrop={onDrop}
        className={cn(
          "flex w-full cursor-pointer flex-col items-center gap-2 rounded-lg border-2 border-dashed border-line-strong bg-surface-2 px-6 py-8 text-center transition-colors hover:border-primary hover:bg-primary-soft disabled:cursor-not-allowed disabled:opacity-50 data-[dragging]:border-primary data-[dragging]:bg-primary-soft",
        )}
      >
        <Icon name="upload-cloud" className="size-7 text-primary-ink" />
        <span className="text-sm font-bold text-fg">{dragging ? l.dragActive : l.title}</span>
        <span id={hintId} className="text-xs text-fg-muted">
          {l.hint}
        </span>
        {children}
      </button>
      <input
        ref={inputRef}
        type="file"
        tabIndex={-1}
        aria-hidden="true"
        className="sr-only"
        accept={accept}
        multiple={multiple}
        onChange={(event) => {
          handle(event.target.files);
          event.target.value = "";
        }}
      />
      <div aria-live="polite" className="empty:hidden mt-2 space-y-1">
        {errors.map((message) => (
          <p key={message} className="flex items-center gap-1.5 text-xs font-semibold text-danger">
            <Icon name="alert-circle" className="size-3.5" />
            {message}
          </p>
        ))}
      </div>
    </div>
  );
}
